import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import ErrorIcon from 'material-ui/svg-icons/alert/error';
import CancelIcon from 'material-ui/svg-icons/navigation/cancel';
import CheckCircleIcon from 'material-ui/svg-icons/action/check-circle';
import CircularProgress from 'material-ui/CircularProgress';
import RefreshIcon from 'material-ui/svg-icons/navigation/refresh';
import SearchIcon from 'material-ui/svg-icons/action/search';
import * as Colors from 'material-ui/styles/colors';
import FlatButton from 'material-ui/FlatButton';
import {
  ERROR_FILE_UPLOAD_STATUS,
  CANCELED_FILE_UPLOAD_STATUS,
  UPLOADED_FILE_UPLOAD_STATUS,
  LOADING_FILE_UPLOAD_STATUS,
  ERROR_IMAGE_DIALOG,
} from 'MyAccount/AssetManager/ImageManager/consts';
import MultiItemStatusBar from 'shared/components/MultiItemStatusBar/MultiItemStatusBar';
import MultiItemStatusBarItem from './MultiItemStatusBar/MultiItemStatusBarItem';
import {openDialog, closeDialog} from 'MyAccount/AssetManager/ImageManager/helpers';
import InlineButton from 'shared/components/InlineButton';
import {ErrorImageDialog} from 'MyAccount/AssetManager/ImageManager/dialogs';
import {isDialogActive} from 'MyAccount/AssetManager/ImageManager/selectors';
import connectWithRouter from 'shared/components/redux/connectWithRouter';

const subHeaderHeight = 40;
const headerHeight = 45;

export const styles = {
  subHeader: {
    height: subHeaderHeight,
    width: '100%',
    background: Colors.grey100,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
};

const getPercentComplete = image => {
  const {percent = 0} = image;
  return _.ceil(0.99 * percent + (image.status === UPLOADED_FILE_UPLOAD_STATUS));
};

const getSmallFileName = (text, size = 35) => {
  return `...${text.substring(text.length - size)}`;
};
@connectWithRouter(state => ({
  openErrorImageDialog: isDialogActive(state, ERROR_IMAGE_DIALOG),
}))
export default class UploadImageStatusBar extends Component {
  state = {
    errorMessage: null,
    currentErrorDialogImage: null,
  };

  onErrorDialogSubmit = () => {
    const {retryUpload} = this.props;
    const {currentErrorDialogImage} = this.state;
    if (currentErrorDialogImage && _.isFunction(retryUpload)) {
      retryUpload(currentErrorDialogImage);
    }
  };

  getSubHeader = () => {
    const {uploadedImages} = this.props;
    const retryAll = _.filter(uploadedImages, image => {
      return image.status === CANCELED_FILE_UPLOAD_STATUS || image.status === ERROR_FILE_UPLOAD_STATUS;
    });
    const errorUpload = _.filter(uploadedImages, image => image.status === ERROR_FILE_UPLOAD_STATUS);
    const single = _.size(retryAll) === 1;
    return (
      <div style={styles.subHeader}>
        <div style={{marginLeft: 20, color: Colors.grey500}}>
          {_.size(retryAll) > 0 &&
            `${_.size(retryAll)} Upload${single ? '' : 's'} ${_.size(errorUpload) > 0 ? 'failed' : 'canceled'}`}
        </div>
        {_.size(retryAll) > 0 ? (
          <FlatButton
            label="RETRY ALL"
            style={{color: Colors.lightGreen700}}
            rippleColor="inherit"
            hoverColor="inherit"
            onClick={() => this.retryAll(retryAll)}
          />
        ) : (
          <FlatButton
            label="CANCEL ALL"
            style={{color: Colors.red800}}
            rippleColor="inherit"
            hoverColor="inherit"
            onClick={this.cancelAll}
          />
        )}
      </div>
    );
  };

  getHeaderLabel = () => {
    const {uploadedImages} = this.props;
    const uploaded = _.filter(uploadedImages, image => image.status === UPLOADED_FILE_UPLOAD_STATUS).length;
    const all = uploadedImages.length;
    return `Uploaded ${uploaded}/${all} images`;
  };

  getContainerHeight = () => {
    const {uploadedImages} = this.props;
    if (_.size(uploadedImages) < 5) {
      return _.size(uploadedImages) * 72 + headerHeight + this.showSubHeader() * subHeaderHeight;
    }
  };

  getRightIconTooltip = image => {
    const {cancelUpload, retryUpload, locateImage} = this.props;
    let tooltip = undefined;
    switch (image.status) {
      case LOADING_FILE_UPLOAD_STATUS:
        if (_.isFunction(cancelUpload)) {
          tooltip = 'Cancel upload';
        }
        break;
      case ERROR_FILE_UPLOAD_STATUS:
      case CANCELED_FILE_UPLOAD_STATUS:
        if (_.isFunction(retryUpload)) {
          tooltip = 'Retry upload';
        }
        break;
      case UPLOADED_FILE_UPLOAD_STATUS:
        if (_.isFunction(locateImage)) {
          tooltip = 'Locate image';
        }
        break;
      default:
        break;
    }
    return tooltip;
  };

  getRightIcon = image => {
    const {cancelUpload, retryUpload, locateImage} = this.props;
    const errorIcon = <ErrorIcon color={Colors.red800} />;
    const checkCircleIcon = <CheckCircleIcon color={Colors.lightGreen700} />;
    const progressIcon = (
      <CircularProgress
        size={20}
        mode={image.percent ? 'determinate' : 'indeterminate'}
        color={Colors.lightBlue500}
        value={image.percent ? getPercentComplete(image) : undefined}
      />
    );
    const errorStatusIcons = retryUpload ? [errorIcon, <RefreshIcon />] : [errorIcon];
    const uploadedStatusIcons = locateImage ? [checkCircleIcon, <SearchIcon />] : [checkCircleIcon];
    const loadingStatusIcons = cancelUpload ? [progressIcon, <CancelIcon />] : [progressIcon];
    switch (image.status) {
      case LOADING_FILE_UPLOAD_STATUS:
        return loadingStatusIcons;
      case UPLOADED_FILE_UPLOAD_STATUS:
        return uploadedStatusIcons;
      case CANCELED_FILE_UPLOAD_STATUS:
      case ERROR_FILE_UPLOAD_STATUS:
        return errorStatusIcons;
      default:
        console.warn('UploadImageStatusBar - Unknown image status: ', image.status);
        return errorStatusIcons;
    }
  };

  getRightIconClick = image => () => {
    const {cancelUpload, retryUpload, locateImage} = this.props;
    switch (image.status) {
      case LOADING_FILE_UPLOAD_STATUS:
        if (_.isFunction(cancelUpload)) {
          cancelUpload(image);
        }
        break;
      case ERROR_FILE_UPLOAD_STATUS:
      case CANCELED_FILE_UPLOAD_STATUS:
        if (_.isFunction(retryUpload)) {
          retryUpload(image);
        }
        break;
      case UPLOADED_FILE_UPLOAD_STATUS:
        if (_.isFunction(locateImage)) {
          locateImage(image);
        }
        break;
      default:
        break;
    }
  };

  getFolderName = folder => {
    if (folder) {
      return folder === '/' ? 'Unsorted' : _.trim(folder, '/');
    }
    return null;
  };

  getSecondaryText = image => {
    const folder = this.getFolderName(image.folder);
    let secondaryText = '';
    switch (image.status) {
      case LOADING_FILE_UPLOAD_STATUS:
        secondaryText = image.percent
          ? `${getPercentComplete(image)}% Uploaded${folder ? ` to ${folder}` : ''}`
          : 'Uploading image...';
        break;
      case UPLOADED_FILE_UPLOAD_STATUS:
        secondaryText = `Image uploaded${folder ? ` to ${folder}` : ''}`;
        break;
      case CANCELED_FILE_UPLOAD_STATUS:
        secondaryText = 'Upload canceled';
        break;
      case ERROR_FILE_UPLOAD_STATUS:
        secondaryText = (
          <span>
            Upload failed with{' '}
            <InlineButton
              style={{color: Colors.red900}}
              onClick={() => {
                const {onErrorButtonClick} = this.props;
                if (_.isFunction(onErrorButtonClick)) {
                  onErrorButtonClick(image);
                } else {
                  this.openErrorImageDialog(image);
                }
              }}
            >
              Error
            </InlineButton>
          </span>
        );
        break;
      default:
        break;
    }
    return secondaryText;
  };

  openErrorImageDialog = image => {
    if (image.errorMessage) {
      const {openCustomErrorImageDialog} = this.props;
      if (_.isFunction(openCustomErrorImageDialog)) {
        openCustomErrorImageDialog(image);
      } else {
        this.setState({errorMessage: image.errorMessage, currentErrorDialogImage: image});
        _.defer(openDialog, {dialogName: ERROR_IMAGE_DIALOG});
      }
    }
  };

  closeErrorImageDialog = () => {
    const {closeCustomErrorImageDialog} = this.props;
    if (_.isFunction(closeCustomErrorImageDialog)) {
      closeCustomErrorImageDialog();
    } else {
      closeDialog();
      this.setState({errorMessage: null, currentErrorDialogImage: null});
    }
  };

  showRemoveAllButton = () => {
    const {uploadedImages} = this.props;
    let show = false;
    if (_.size(uploadedImages) > 0) {
      show = !_.size(_.filter(uploadedImages, image => image.status === LOADING_FILE_UPLOAD_STATUS));
    }
    return show;
  };

  cancelAll = () => {
    const {uploadedImages, cancelUpload} = this.props;
    _.forEach(uploadedImages, image => {
      if (image.status === LOADING_FILE_UPLOAD_STATUS && _.isFunction(cancelUpload)) {
        cancelUpload(image);
      }
    });
  };

  retryAll = imagesToRetry => {
    const {retryUpload} = this.props;
    _.forEach(imagesToRetry, image => {
      if (_.isFunction(retryUpload)) {
        retryUpload(image);
      }
    });
  };

  showSubHeader = () => {
    const {uploadedImages} = this.props;
    return !_.every(uploadedImages, ['status', UPLOADED_FILE_UPLOAD_STATUS]);
  };

  render() {
    const {uploadedImages, removeAll, openErrorImageDialog, fixedPosition, actionBarMargin} = this.props;
    const {errorMessage} = this.state;
    const showSubHeader = this.showSubHeader();
    return _.size(uploadedImages) > 0 ? (
      <div>
        <MultiItemStatusBar
          subHeader={showSubHeader ? this.getSubHeader : null}
          headerLabel={this.getHeaderLabel()}
          removeAll={removeAll}
          showRemoveAllButton={this.showRemoveAllButton()}
          containerHeight={this.getContainerHeight()}
          headerHeight={headerHeight}
          subHeaderHeight={showSubHeader ? subHeaderHeight : 0}
          fixedPosition={fixedPosition}
          actionBarMargin={actionBarMargin}
        >
          {_.map(uploadedImages, (image, index) => {
            return (
              <MultiItemStatusBarItem
                id={`cla-image-${index}-status`}
                key={index}
                rightIconButton={this.getRightIcon(image)}
                rightIconClick={this.getRightIconClick(image)}
                rightIconTooltip={this.getRightIconTooltip(image)}
                tooltipPosition="bottom-left"
                primaryText={image.name.length > 35 ? getSmallFileName(image.name) : image.name}
                thumbUrl={image.url}
                secondaryText={this.getSecondaryText(image, index)}
              />
            );
          })}
        </MultiItemStatusBar>
        <ErrorImageDialog
          open={openErrorImageDialog}
          errorMessage={errorMessage}
          onSubmit={this.onErrorDialogSubmit}
          closeDialog={this.closeErrorImageDialog}
        />
      </div>
    ) : null;
  }
}

UploadImageStatusBar.propTypes = {
  uploadedImages: PropTypes.array.isRequired,
  removeAll: PropTypes.func.isRequired,
  cancelUpload: PropTypes.func,
  retryUpload: PropTypes.func,
  locateImage: PropTypes.func,
  onErrorButtonClick: PropTypes.func,
  openCustomErrorImageDialog: PropTypes.func,
  closeCustomErrorImageDialog: PropTypes.func,
};
